import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Text } from '@abyss/web/ui/Text';
import { MutableRefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLagoon } from '../../hooks/useLagoon';
import { ProviderLocation } from '../../models/ProviderDetails';
import { getDistance } from '../../utils/card.utils';
import { isProviderStartDateInFuture } from '../../utils/date.utils';
import { getFormattedAddress } from '../../utils/providerDetails.utils';
import { capitalizeFirstLetters } from '../../utils/providerNames.utils';
import { getCurrentMember } from '../../utils/user.utils';
import { Constants, LOB } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { phoneOnly } from '../ConstantsStyles';
import { InNetworkBadgeWithDate } from '../InNetworkBadge/InNetworkBadge';
import { TerminationBadge } from '../TerminationBadge/TerminationBadge';
import { getFeatureFlag } from '../Utils';
import { handleLinkAndModalTrack } from '../Utils/adobeTrackUtils';
import {
  AcceptingNewPatientsBadge,
  LocationBadgeRow,
  LocationCardContainer,
  LocationCardStack,
  LocationCardWrapper,
  LocationDetailsStack,
  MilesButton,
  SelectLocationBadge,
  SelectLocationButton,
} from './SelectLocationCard.styled';

export type Props = {
  className?: string;
  handleDirectionClick: () => void;
  highlightRefs: MutableRefObject<any>;
  isMobileMapView?: boolean;
  index?: number;
  location: ProviderLocation;
  selected?: boolean;
  selectLocation(): void;
  setIsMobileMapView: (isMobileMapView: boolean) => void;
  showBadgeRow?: boolean;
  totalLocations?: number;
  selectedId: string | null;
};

export const SelectLocationCard = ({
  location,
  isMobileMapView = false,
  index = 0,
  showBadgeRow = false,
  selected = false,
  highlightRefs,
  handleDirectionClick,
  selectLocation,
  totalLocations = 0,
  className = '',
  selectedId,
}: Props) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(phoneOnly);

  const {
    address,
    distance,
    acceptingNewPatients,
    providerGroupsAffiliations,
    networks,
  } = location;

  const networksArray = Array.isArray(networks) ? networks : [networks];
  const allNetworkAffiliations = networksArray.flatMap(
    (n) => n?.networkAffiliations ?? []
  );
  const hasNetworkAffiliations = allNetworkAffiliations.length > 0;

  const currentMember = getCurrentMember();

  const hasValidNetworkAffiliations =
    hasNetworkAffiliations &&
    (currentMember?.lineOfBusiness === LOB.MNR ||
      currentMember?.lineOfBusiness === LOB.IFP ||
      currentMember?.lineOfBusiness === LOB.CNS ||
      currentMember?.isUspNiceMember) &&
    allNetworkAffiliations.some(
      (na) =>
        na.acceptingNewPatients &&
        (na.pcpIndicator === true || na.pcpIndicator?.toString() === 'true')
    );

  const [includeProviderGroupName] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.INCLUDE_PROVIDER_GROUP_NAME,
  ]);
  const featureFlags = useLagoon('feature-flags')();
  const displayInNetworkBadgeEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_IN_NETWORK_BADGE_FUTURE_START_DATE
  );
  const enableNetworkAffiliationFlow: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NETWORK_AFFILIATION_FLOW
  );
  const shouldApplyNetworkAffiliationLogic =
    enableNetworkAffiliationFlow && hasValidNetworkAffiliations;

  const displayInNetworkBadge = isProviderStartDateInFuture(
    location?.network?.start
  );

  const addressString = getFormattedAddress(address, includeProviderGroupName);

  const choosePcpLocationTestId: string = 'choose-pcp-location';
  const selectLocationCardTestId: string = 'pcp-select-location-card';
  const selectedLocationDevRef = useRef<HTMLDivElement>(null);

  const handleSelectLocation = () => {
    const linkName: string = selectLocationCardTestId;
    const linkLocation: string = `body:${choosePcpLocationTestId}`;
    const modalName: string = Constants.MAP_VIEW_NAVIGATION_MODAL;
    const linkType: string = 'radio';
    handleLinkAndModalTrack(linkName, linkLocation, modalName, linkType);
    selectLocation();
  };
  useEffect(() => {
    if (selectedId && selectedLocationDevRef?.current) {
      selectedLocationDevRef?.current?.focus();
    }
  }, [selectedId]);

  return (
    <LocationCardContainer
      data-auto-testid="select-location-card"
      data-testid="select-location-card"
      onClick={() => {
        handleDirectionClick();
      }}
    >
      <LocationCardStack>
        <LocationDetailsStack
          alignItems="left"
          className="location-details"
          grow="true"
        >
          <div>
            <Text
              color="$gray8"
              fontWeight="$medium"
              size={isMobileMapView ? 'sm' : 'md'}
            >
              {t('CHOOSE_PCP_FLOW.LOCATION_NUMBER', {
                index: index + 1,
                total: totalLocations,
              })}
            </Text>
          </div>
          <LocationCardWrapper
            aria-pressed={selected ? 'true' : 'false'}
            className={`location-card-wrap ${className}`}
            data-auto-testid={selectLocationCardTestId}
            data-testid={selectLocationCardTestId}
            style={{
              width: '100%',
            }}
          >
            <TerminationBadge
              endDate={location?.network?.end}
              message={t('PCP_TERMINATION.MAY_LEAVE_THE_NETWORK_ON')}
            />
            {displayInNetworkBadge && displayInNetworkBadgeEnabled && (
              <InNetworkBadgeWithDate
                message={t('DISPLAY_IN_NETWORK_BADGE.IN_NETWORK_BADGE_PCP')}
                startDate={location?.network?.start}
              />
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {includeProviderGroupName &&
                !shouldApplyNetworkAffiliationLogic &&
                providerGroupsAffiliations?.[0] && (
                  <Text
                    color="$gray8"
                    fontWeight="$bold"
                    size={isMobileMapView ? 'md' : 'lg'}
                  >
                    {capitalizeFirstLetters(
                      providerGroupsAffiliations[0]?.value
                    )}
                  </Text>
                )}
            </div>
            <Text
              color="$gray8"
              fontWeight="$medium"
              size={isMobileMapView ? 'sm' : 'md'}
            >
              {addressString}
            </Text>
          </LocationCardWrapper>
          {showBadgeRow ? (
            <LocationBadgeRow
              data-auto-testid="select-location-badge-row"
              data-testid="select-location-badge-row"
            >
              <MilesButton
                data-auto-testid="select-location-miles-button"
                data-testid="select-location-miles-button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDirectionClick();
                }}
                ref={(el: HTMLButtonElement) => {
                  if (highlightRefs) {
                    highlightRefs.current[location.locationId] = el;
                  }
                }}
              >
                <IconMaterial icon="directions" size="$sm" />
                <Text color="$interactive1" fontWeight="$bold" size="$md">
                  {getDistance(distance)} {t('miles')}
                </Text>
              </MilesButton>

              {acceptingNewPatients && !shouldApplyNetworkAffiliationLogic ? (
                <div style={{ paddingLeft: '10px' }}>
                  <AcceptingNewPatientsBadge
                    icon={
                      <IconMaterial color="$success1" icon="check" size="sm" />
                    }
                    variant="none"
                  >
                    <Text color="$gray8" fontWeight="$medium" size="$md">
                      {t('Accepting new patients')}
                    </Text>
                  </AcceptingNewPatientsBadge>
                </div>
              ) : null}
            </LocationBadgeRow>
          ) : null}
        </LocationDetailsStack>
        {selected ? (
          <div
            className="seledted-location-name"
            ref={selectedLocationDevRef}
            tabIndex={-1}
          >
            <SelectLocationBadge
              icon={
                <IconSymbol
                  color="$success1"
                  icon="check_circle"
                  size="$sm"
                  variant="filled"
                />
              }
              onClick={() => {
                selectLocation();
              }}
              variant="success"
            >
              {t('CHOOSE_PCP_FLOW.SELECTED_LOCATION')}
            </SelectLocationBadge>
          </div>
        ) : (
          <SelectLocationButton
            aria-label={`${t(
              'CHOOSE_PCP_FLOW.CHOOSE_THIS_LOCATION'
            )} ${addressString} ${getDistance(distance)} ${t('miles')}`}
            data-testid="choose-location-button"
            onClick={(event) => {
              event?.preventDefault();
              event?.stopPropagation();
              handleSelectLocation();
              // Mobile view user not able to choose location, due to page navigating to navigation map view.
              if (!mobileScreen) {
                handleDirectionClick();
              }
            }}
            variant="outline"
          >
            {t('CHOOSE_PCP_FLOW.CHOOSE_THIS_LOCATION')}
          </SelectLocationButton>
        )}
      </LocationCardStack>
    </LocationCardContainer>
  );
};
